import type {
  CloseRequest,
  DraftRequest,
  EpisodeEvent,
  EpisodeObservation,
  EpisodeSummary,
  EpisodeTarget,
  LinkedAction,
  LinkedOutreach,
  ObservationRequest,
  ObservationResult,
  ProposalDecision,
  ProposalDecisionRequest,
  RecommendationLifecycleEvent,
} from '@troyai/portal/intervention-episodes/data-access';
import { EPISODE_AGENT_IDENTITY } from '@troyai/portal/intervention-episodes/data-access';
import {
  DEMO_ACTORS,
  demoInstant,
  demoNameFor,
  demoPersona,
  isPharmacyDemo,
} from '@troyai/portal/investor-demo/core';

import type { DemoData, DemoEpisode, Visible } from './demo-data';
import { seedActions } from './fixtures/patient.fixture';

/**
 * The episode commands of the fake backend. Each one changes the stored episode the way the real
 * backend does where the screens depend on it, appends an `EpisodeEvent` plus the matching
 * `recommendation_lifecycle` row, and bumps `version` and `updated_at`. A command that does not
 * apply to the episode's status changes nothing, so a double click is harmless. One the backend
 * refuses for who sends it, or for a bad assignee, throws `EpisodeCommandRefused` before changing
 * anything.
 */

export type EpisodeCommand =
  | { kind: 'activate' }
  | { kind: 'cancel'; reason: string }
  | { kind: 'decide'; proposalId: number; body: ProposalDecisionRequest }
  | { kind: 'observe'; targetId: number; body: ObservationRequest }
  | { kind: 'reassign'; ownerIdentity: string }
  | { kind: 'assign-target'; targetId: number; assigneeIdentity: string }
  | { kind: 'close'; body: CloseRequest }
  | { kind: 'link-action'; actionId: number }
  | { kind: 'link-outreach'; outreachId: number }
  | { kind: 'demo-assign' }
  | { kind: 'replace-draft'; body: DraftRequest };

interface Ctx {
  data: DemoData;
  episode: DemoEpisode;
  /** Demo "now", written without an offset. */
  now: string;
  /** Dana's email: the demo's own actions are recorded under her. */
  actor: string;
}

/** A command the backend answers with an error: its status and the message in the body. */
export class EpisodeCommandRefused extends Error {
  constructor(
    readonly status: 400 | 403 | 409,
    message: string
  ) {
    super(message);
  }
}

const INVALID = 'invalid intervention episode command: ';
const NOT_PERMITTED = 'intervention episode command not permitted: ';

const refused = (status: 400 | 403 | 409, reason: string): EpisodeCommandRefused =>
  new EpisodeCommandRefused(status, (status === 400 ? INVALID : NOT_PERMITTED) + reason);

const PROPOSAL_LABELS: Record<ProposalDecision, string> = {
  implemented: 'Implemented',
  not_implemented: 'Not implemented',
  declined: 'Declined',
};

const RESULT_LABELS: Record<ObservationResult, string> = {
  met: 'Met',
  not_met: 'Not met',
  indeterminate: 'Indeterminate',
};

/** The task the "Assign to Lisa" button creates (fixed wording, see the run sheet). */
const ASSIGN_TITLE = 'Task assigned to Lisa Reyes';
const ASSIGN_NOTE = "Call Dr. Patel's office directly about stopping meloxicam.";

/** Rows stay oldest first (the page reverses them), so a new row goes after every row not later. */
function insertByTime<T extends { occurred_at: string }>(rows: T[], row: T): void {
  let at = rows.length;
  while (at > 0 && (rows[at - 1]?.occurred_at ?? '') > row.occurred_at) {
    at--;
  }
  rows.splice(at, 0, row);
}

function record(ctx: Ctx, type: string, title: string, note?: string): void {
  recordPayload(ctx, type, () => (note ? { title, note } : { title }));
}

/** `payload` is called once per copy, so changing one copy never changes the other. */
function recordPayload(ctx: Ctx, type: string, payload: () => Record<string, unknown>): void {
  const { data, episode, now, actor } = ctx;
  const eventId = data.nextId++;
  const event: Visible<EpisodeEvent> = {
    event_id: eventId,
    event_type: type,
    actor_identity: actor,
    payload: payload(),
    occurred_at: now,
  };
  const row: Visible<RecommendationLifecycleEvent> = {
    source_kind: 'episode_event',
    source_id: eventId,
    event_type: type,
    occurred_at: now,
    payload: payload(),
  };
  insertByTime(episode.events, event);
  insertByTime(episode.recommendation_lifecycle, row);
  episode.version += 1;
  episode.updated_at = now;
}

const isOpen = (episode: DemoEpisode): boolean =>
  episode.status === 'draft' || episode.status === 'active';

function activate(ctx: Ctx): void {
  const { episode, now, actor } = ctx;
  if (episode.status !== 'draft') {
    return;
  }
  episode.status = 'active';
  episode.activated_at = now;
  episode.activated_by = actor;
  record(ctx, 'activated', 'Episode activated');
}

function cancel(ctx: Ctx, reason: string): void {
  const { episode, now, actor } = ctx;
  if (!isOpen(episode)) {
    return;
  }
  episode.status = 'cancelled';
  episode.closed_at = now;
  episode.closed_by = actor;
  episode.closure_reason = reason;
  record(ctx, 'cancelled', 'Episode cancelled', reason || undefined);
}

function decide(ctx: Ctx, proposalId: number, body: ProposalDecisionRequest): void {
  const { episode, now, actor } = ctx;
  const proposal = episode.proposals.find((candidate) => candidate.proposal_id === proposalId);
  if (episode.status !== 'active' || !proposal || !Object.hasOwn(PROPOSAL_LABELS, body.status)) {
    return;
  }
  const reason = typeof body.reason === 'string' ? body.reason.trim() : '';
  if (proposal.status === body.status && (proposal.decision_reason ?? '') === reason) {
    return;
  }
  proposal.status = body.status;
  proposal.decided_by = actor;
  proposal.decided_at = now;
  proposal.decision_reason = reason;
  record(
    ctx,
    'proposal_decided',
    `Proposal decision recorded: ${PROPOSAL_LABELS[body.status]}`,
    reason || undefined
  );
}

/** Emails are compared without case; an empty identity (unassigned) is nobody's. */
const sameIdentity = (a: string | undefined, b: string): boolean =>
  !!a && a.toLowerCase() === b.toLowerCase();

const time = (instant: string): number => new Date(instant).getTime();

/** The latest result is that of the observation assessed last; a tie goes to the newer one. */
function latestResult(observations: EpisodeObservation[]): ObservationResult | undefined {
  let latest: EpisodeObservation | undefined;
  for (const observation of observations) {
    if (!latest || time(observation.assessed_at) >= time(latest.assessed_at)) {
      latest = observation;
    }
  }
  return latest?.result;
}

function observe(ctx: Ctx, targetId: number, body: ObservationRequest): void {
  const { data, episode, now, actor } = ctx;
  const target = episode.targets.find((candidate) => candidate.target_id === targetId);
  if (episode.status !== 'active' || !target || !Object.hasOwn(RESULT_LABELS, body.result)) {
    return;
  }
  // As on the backend: the target's assignee or a pharmacist of the member's pharmacy
  if (!isPharmacyDemo() && !sameIdentity(target.assignee_identity, actor)) {
    throw target.assignee_identity
      ? refused(
          403,
          "only the target's assignee or a pharmacist of the member's pharmacy may record observations"
        )
      : refused(409, 'the target must be assigned before observations are recorded');
  }
  const observation: EpisodeObservation = {
    observation_id: data.nextId++,
    target_id: target.target_id,
    result: body.result,
    assessed_value: null,
    ...(body.clinical_event_id !== undefined ? { clinical_event_id: body.clinical_event_id } : {}),
    ...(body.case_note_id !== undefined ? { case_note_id: body.case_note_id } : {}),
    ...(body.action_global_id !== undefined ? { action_global_id: body.action_global_id } : {}),
    ...(body.manual_author ? { manual_author: body.manual_author } : {}),
    ...(body.manual_note ? { manual_note: body.manual_note } : {}),
    recorded_by: actor,
    assessed_at: body.assessed_at ?? now,
    recorded_at: now,
  };
  target.observations.push(observation);
  target.latest_result = latestResult(target.observations);
  record(
    ctx,
    'observation_recorded',
    `Observation recorded: ${target.description} (${RESULT_LABELS[body.result]})`,
    body.manual_note || undefined
  );
}

/**
 * Who the backend accepts as an assignee: Troy Agent (a fixed identity, not an email), a care
 * manager (Dana stands in for "an enabled CM in the tenant"), and a pharmacist of the member's
 * pharmacy: the persona in the pharmacy perspective, Lisa in the care one.
 */
function acceptedAssignees(): string[] {
  const pharmacist = isPharmacyDemo() ? demoPersona() : DEMO_ACTORS.lisa;
  const careManagers = Object.values(DEMO_ACTORS).filter((actor) => actor.kind === 'coordinator');
  return [EPISODE_AGENT_IDENTITY, ...careManagers.map((actor) => actor.email), pharmacist.email];
}

function assignTarget(ctx: Ctx, targetId: number, requested: string): void {
  const { episode } = ctx;
  if (episode.status !== 'active') {
    throw refused(409, 'targets can be assigned only on an active episode');
  }
  const wanted = requested.trim();
  // The agent's identity is matched exactly; emails without case
  const assignee = acceptedAssignees().find((candidate) =>
    candidate === EPISODE_AGENT_IDENTITY ? candidate === wanted : sameIdentity(candidate, wanted)
  );
  if (!assignee) {
    throw refused(
      400,
      `assignee_identity must be "${EPISODE_AGENT_IDENTITY}", the email of an enabled care manager in this tenant, or a pharmacist of the member's pharmacy`
    );
  }
  const target = episode.targets.find((candidate) => candidate.target_id === targetId);
  const previous = target?.assignee_identity ?? '';
  if (!target || previous === assignee) {
    return;
  }
  target.assignee_identity = assignee;
  recordPayload(ctx, 'target_assigned', () => ({
    target_id: target.target_id,
    previous_assignee_identity: previous,
    assignee_identity: assignee,
  }));
}

function reassign(ctx: Ctx, ownerIdentity: string): void {
  const { episode } = ctx;
  const owner = ownerIdentity.trim();
  if (!isOpen(episode) || !owner || owner === episode.owner_identity) {
    return;
  }
  episode.owner_identity = owner;
  record(ctx, 'owner_reassigned', `Owner changed to ${demoNameFor(owner) ?? owner}`);
}

function close(ctx: Ctx, body: CloseRequest): void {
  const { episode, now, actor } = ctx;
  if (
    episode.status !== 'active' ||
    (body.status !== 'resolved' && body.status !== 'not_achieved') ||
    (body.status === 'resolved' &&
      episode.targets.some((target) => target.required && target.latest_result !== 'met'))
  ) {
    return;
  }
  const reason = typeof body.reason === 'string' ? body.reason.trim() : '';
  episode.status = body.status;
  episode.closed_at = now;
  episode.closed_by = actor;
  episode.closure_reason = reason;
  record(
    ctx,
    'closed',
    `Closed: ${body.status === 'resolved' ? 'Resolved' : 'Not achieved'}`,
    reason || undefined
  );
}

function linkAction(ctx: Ctx, actionId: number): void {
  const { episode, now, actor } = ctx;
  if (!isOpen(episode) || episode.activity.actions.some((a) => a.action_global_id === actionId)) {
    return;
  }
  const known = seedActions().find((candidate) => candidate.global_id === actionId);
  const title = known?.title ?? `Action ${actionId}`;
  const linked: LinkedAction = {
    action_global_id: actionId,
    action_template_id: known?.action_template_id ?? 0,
    title,
    status: known?.status ?? 'In Review',
    ...(known?.due_date ? { due_date: known.due_date } : {}),
    created_at: known?.timestamp ?? now,
    linked_by: actor,
    linked_at: now,
  };
  episode.activity.actions.push(linked);
  record(ctx, 'action_linked', 'Action linked', title);
}

function linkOutreach(ctx: Ctx, outreachId: number): void {
  const { episode, now, actor } = ctx;
  const linked = episode.activity.outreach_attempts;
  if (!isOpen(episode) || linked.some((o) => o.member_outreach_id === outreachId)) {
    return;
  }
  const outreach: LinkedOutreach = {
    member_outreach_id: outreachId,
    outreach_channel: 'voice-call',
    direction: 'outbound',
    outcome: 'completed',
    purposes: ['care_coordination'],
    created_by: actor,
    created_at: now,
    directly_linked: true,
  };
  linked.push(outreach);
  record(ctx, 'outreach_linked', 'Outreach linked');
}

/** "Assign to Lisa" on the escalation row: once per episode. */
function demoAssign(ctx: Ctx): void {
  const { episode } = ctx;
  if (episode.status !== 'active' || episode.events.some((e) => e.event_type === 'task_assigned')) {
    return;
  }
  record(ctx, 'task_assigned', ASSIGN_TITLE, ASSIGN_NOTE);
}

/** The backend sends the due day as midnight UTC; the draft form sends `yyyy-MM-dd` or `''`. */
function dueDate(day: string): string | undefined {
  if (!day) {
    return undefined;
  }
  return day.includes('T') ? day : `${day}T00:00:00Z`;
}

/** Replaces the draft's definition. Targets keep their id (and history) when the metric matches. */
function replaceDraft(ctx: Ctx, body: DraftRequest): void {
  const { data, episode, now } = ctx;
  if (episode.status !== 'draft' || !Array.isArray(body.targets)) {
    return;
  }
  const pool = [...episode.targets];
  episode.objective = body.objective;
  episode.owner_identity = body.owner_identity;
  episode.targets = body.targets.map((request): EpisodeTarget => {
    const at = pool.findIndex((candidate) => candidate.metric_key === request.metric_key);
    const kept = at >= 0 ? pool.splice(at, 1)[0] : undefined;
    const due = dueDate(request.due_date);
    return {
      target_id: kept?.target_id ?? data.nextId++,
      metric_key: request.metric_key,
      description: request.description,
      baseline: request.baseline,
      required: request.required,
      ...(due ? { due_date: due } : {}),
      evaluation_contract: kept?.evaluation_contract ?? null,
      created_at: kept?.created_at ?? now,
      observations: kept?.observations ?? [],
      // As on the backend, a draft edit re-creates the targets: assignment waits for activation
      assignee_identity: '',
    };
  });
  record(ctx, 'draft_updated', 'Draft edited');
}

/** What the pharmacy perspective's pharmacist may not do: the care manager roles keep these. */
const CARE_MANAGER_ONLY: Partial<Record<EpisodeCommand['kind'], string>> = {
  decide: 'decide proposals',
  'link-action': 'link actions',
  'link-outreach': 'link outreach',
  reassign: 'reassign the owner',
  close: 'close an episode',
  cancel: 'cancel an episode',
};

/**
 * The backend's role and owner rules, checked before a command looks at the episode. In the
 * pharmacy perspective the persona is a pharmacist: she edits drafts and activates only on her
 * own episodes and leaves the rest to the care manager roles. Assigning is the owner's for
 * everyone.
 */
function checkAllowed({ episode, actor }: Ctx, command: EpisodeCommand): void {
  const pharmacist = isPharmacyDemo();
  const owner = sameIdentity(episode.owner_identity, actor);
  const careManagerOnly = CARE_MANAGER_ONLY[command.kind];
  if (pharmacist && careManagerOnly) {
    throw refused(403, `only a care manager may ${careManagerOnly}`);
  }
  if (!owner && command.kind === 'assign-target') {
    throw refused(403, 'only the episode owner may assign its targets');
  }
  if (pharmacist && !owner && command.kind === 'activate') {
    throw refused(403, 'only the episode owner may activate it');
  }
  if (pharmacist && !owner && command.kind === 'replace-draft') {
    throw refused(403, 'only the episode owner may edit its draft');
  }
}

/**
 * Applies `command` to the episode inside `data` (call it from `DemoStore.mutate`). Returns the
 * stored episode, changed or not, or null when no episode has that id. Throws
 * `EpisodeCommandRefused` for a command the backend would refuse.
 */
export function applyEpisodeCommand(
  data: DemoData,
  episodeId: number,
  command: EpisodeCommand
): DemoEpisode | null {
  const episode = data.episodes.find(
    (candidate) => candidate.intervention_episode_id === episodeId
  );
  if (!episode) {
    return null;
  }
  const ctx: Ctx = { data, episode, now: demoInstant(), actor: demoPersona().email };
  checkAllowed(ctx, command);
  switch (command.kind) {
    case 'activate':
      activate(ctx);
      break;
    case 'cancel':
      cancel(ctx, command.reason);
      break;
    case 'decide':
      decide(ctx, command.proposalId, command.body);
      break;
    case 'observe':
      observe(ctx, command.targetId, command.body);
      break;
    case 'reassign':
      reassign(ctx, command.ownerIdentity);
      break;
    case 'assign-target':
      assignTarget(ctx, command.targetId, command.assigneeIdentity);
      break;
    case 'close':
      close(ctx, command.body);
      break;
    case 'link-action':
      linkAction(ctx, command.actionId);
      break;
    case 'link-outreach':
      linkOutreach(ctx, command.outreachId);
      break;
    case 'demo-assign':
      demoAssign(ctx);
      break;
    case 'replace-draft':
      replaceDraft(ctx, command.body);
      break;
  }
  return episode;
}

/**
 * One card of the member's episode list. Like the backend: the next due date belongs to an
 * active episode only and is the earliest due day among targets not yet met, and the progress
 * counts the required targets. `today` is accepted so callers read like the backend's
 * date-based query, but nothing here depends on it.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- `today` is part of the call shape
export function episodeSummary(episode: DemoEpisode, today: string): EpisodeSummary {
  const required = episode.targets.filter((target) => target.required);
  const open = episode.targets.filter(
    (target): target is EpisodeTarget & { due_date: string } =>
      !!target.due_date && target.latest_result !== 'met'
  );
  const nextDue =
    episode.status === 'active' ? open.map((target) => target.due_date).sort()[0] : undefined;
  return {
    intervention_episode_id: episode.intervention_episode_id,
    member_global_id: episode.member_global_id,
    status: episode.status,
    objective: episode.objective,
    owner_identity: episode.owner_identity,
    ...(episode.previous_intervention_episode_id !== undefined
      ? { previous_intervention_episode_id: episode.previous_intervention_episode_id }
      : {}),
    version: episode.version,
    created_at: episode.created_at,
    updated_at: episode.updated_at,
    ...(nextDue ? { next_due_date: nextDue } : {}),
    ...(required.length > 0
      ? {
          target_progress: {
            required: required.length,
            met: required.filter((target) => target.latest_result === 'met').length,
          },
        }
      : {}),
    ...(episode.care_plan ? { care_plan: episode.care_plan } : {}),
    source_recommendation_rejected: false,
  };
}
